import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { PhoneIntakeInput, PhoneIntakeResult, StaffIntakeBody } from "./types";

// Mirror of the createAssetSchema VEHICLE constraints (packages/validation/src/asset.ts) so
// the browser only forwards values the strict server DTO will accept — an invalid plate/vin
// is treated as "not provided" rather than a 400. Source of record stays server-side.
const PLATE_RE = /^[0-9]{2}\s?[A-Z]{1,3}\s?[0-9]{2,4}$/;
const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/;
const UAVT_RE = /^\d{10}$/;
const VEHICLE_LINES = new Set(["trafik", "kasko"]);
// webIntakeDwellingSchema.structure is a fixed enum; the console field is free text,
// so a value outside the set is OMITTED rather than sent and rejected. The rest of
// the dwelling block still lands — one unrecognised word must not fail the intake.
const STRUCTURES = new Set(["betonarme", "celik", "yigma", "diger"]);

/**
 * A3 — phone intake as ONE call.
 *
 * This hook used to run the whole cold-lead chain as SIX sequential POSTs from the
 * browser (party → household → member → asset → lead → interaction). Each one
 * committed on its own, so a failure partway through left a real party and
 * household in the database with no lead and no consent record — while the advisor
 * was still on the phone with the caller. Retrying either created a second customer
 * or hit dedup and looked like an error, and no screen showed what had landed.
 *
 * It is now a single POST /v1/intake/leads, executed server-side in ONE transaction
 * (StaffIntakeService). Either the whole intake exists or none of it does.
 *
 * Note what disappeared with the chain: the try/catch that let an asset failure
 * through as `assetError` while the lead continued. Inside one transaction there is
 * no "partly succeeded" to report — an optional vehicle is expressed by OMITTING
 * it, not by tolerating a failed write.
 */
export function usePhoneIntake() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PhoneIntakeInput): Promise<PhoneIntakeResult> =>
      api.post<PhoneIntakeResult>("/v1/intake/leads", toStaffIntakeBody(input)),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["leads"] }),
  });
}

/**
 * Flatten what the advisor typed into the server's nested boundary shape
 * (staffIntakeSchema). That schema is `.strict()` AND line-gated — a vehicle block
 * on a health lead is a 400, not a silent drop — so each block is included ONLY for
 * the line it belongs to.
 *
 * Blank strings become absent keys, so the strict DTO sees a genuinely undefined
 * optional rather than an empty string. An invalid plate/VIN/UAVT is likewise
 * omitted: a caller often does not have the document to hand, and a half-remembered
 * plate must not fail the whole intake.
 *
 * Exported for the test: the mapping is where a field silently goes missing, which
 * is the exact failure this sprint keeps finding, so it is asserted directly.
 */
export function toStaffIntakeBody(input: PhoneIntakeInput): StaffIntakeBody {
  const t = (v: string | undefined): string | undefined => {
    const x = v?.trim();
    return x && x.length > 0 ? x : undefined;
  };

  const plate = input.plate?.trim().toUpperCase();
  const vin = input.vin?.trim().toUpperCase();
  const validPlate = plate && PLATE_RE.test(plate) ? plate : undefined;
  const validVin = vin && VIN_RE.test(vin) ? vin : undefined;
  const label =
    [input.make, input.model]
      .map((v) => v?.trim())
      .filter((v): v is string => Boolean(v))
      .join(" ")
      .trim() || undefined;

  const uavt = t(input.uavtCode);
  const validUavt = uavt && UAVT_RE.test(uavt) ? uavt : undefined;

  const body: StaffIntakeBody = {
    product: input.line,
    person: {
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      phone: input.phone.trim(),
      tckn: input.tckn.trim(),
      ...(t(input.email) ? { email: t(input.email) } : {}),
      ...(t(input.birthDate) ? { birthDate: t(input.birthDate) } : {}),
      ...(t(input.il) ? { il: t(input.il) } : {}),
      ...(t(input.ilce) ? { ilce: t(input.ilce) } : {}),
      ...(t(input.meslek) ? { meslek: t(input.meslek) } : {}),
    },
    consents: {
      // The advisor's attestation. Typed server-side as z.literal(true), so an
      // intake that skipped the aydınlatma confirmation is rejected at the boundary
      // rather than stored as a lead with no consent behind it.
      service: true,
      ...(input.marketingConsent ? { marketing: true } : {}),
    },
  };

  // VEHICLE (trafik/kasko). The shared block requires plate OR vin, so it rides only
  // when one validated; otherwise the line stays lead-only and the advisor captures
  // the vehicle later.
  if (VEHICLE_LINES.has(input.line) && (validPlate || validVin)) {
    body.vehicle = {
      ...(validPlate ? { plate: validPlate } : {}),
      ...(validVin ? { vin: validVin } : {}),
      ...(label ? { label } : {}),
      ...(t(input.ruhsatSerno) ? { ruhsatSerno: t(input.ruhsatSerno) } : {}),
      ...(t(input.ehliyetDate) ? { ehliyetDate: t(input.ehliyetDate) } : {}),
    };
  }

  // DWELLING (dask) — the same §3 attrs the web door sends.
  if (input.line === "dask") {
    const structure = t(input.yapiTarzi)?.toLowerCase();
    const dwelling: NonNullable<StaffIntakeBody["dwelling"]> = {
      ...(validUavt ? { uavtCode: validUavt } : {}),
      ...(t(input.address) ? { address: t(input.address) } : {}),
      ...(t(input.brutM2) ? { grossM2: t(input.brutM2) } : {}),
      ...(structure && STRUCTURES.has(structure) ? { structure } : {}),
      ...(t(input.insaYili) ? { buildYear: t(input.insaYili) } : {}),
      ...(t(input.kat) ? { floor: t(input.kat) } : {}),
    };
    if (Object.keys(dwelling).length > 0) body.dwelling = dwelling;
  }

  if (input.line === "seyahat") {
    const travel: NonNullable<StaffIntakeBody["travel"]> = {
      ...(t(input.travelDestination) ? { destination: t(input.travelDestination) } : {}),
      ...(t(input.travelStart) ? { startDate: t(input.travelStart) } : {}),
      ...(t(input.travelEnd) ? { endDate: t(input.travelEnd) } : {}),
      ...(t(input.travelers) ? { travelers: t(input.travelers) } : {}),
    };
    if (Object.keys(travel).length > 0) body.travel = travel;
  }

  if (input.line === "saglik") {
    const health: NonNullable<StaffIntakeBody["health"]> = {
      ...(t(input.sgkStatus) ? { sgkStatus: t(input.sgkStatus) } : {}),
      ...(t(input.hospitalPref) ? { hospitalPref: t(input.hospitalPref) } : {}),
      // The health DECLARATION is never captured here — this only ACKs that it is
      // handled out-of-band by the advisor.
      declarationOffline: true,
      // The advisor's attestation that the special-category açık rıza was given
      // verbally. Deliberately a DIFFERENT field from the web door's self-tick, so
      // the evidence never claims the subject granted it directly.
      ...(input.healthConsent ? { staffAttestedConsent: true } : {}),
    };
    body.health = health;
  }

  return body;
}
